import { Box, Typography } from "@mui/material";
import type { ReactNode } from "react";

/** Where GFG's fiscal year starts — the "owed at" date on the organization list. */
export const FY_START_LABEL = "1 Jan";

/** 1234.5 → "1,234.50". Negative amounts keep a real minus sign. */
export const money = (v: string | null | undefined) =>
  Number(v ?? 0)
    .toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    .replace("-", "−");

/** "2026-10" → "Oct 2026". */
export const periodLabel = (period: string) =>
  new Date(`${period}-01T12:00:00Z`).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });

/** "2026-10-31" → "Oct 31". */
export const shortDay = (day: string) =>
  new Date(`${day}T12:00:00Z`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });

export const th = {
  fontSize: 11,
  letterSpacing: "0.6px",
  textTransform: "uppercase" as const,
  color: "var(--color-graphite)",
  fontWeight: 600,
  borderBottom: "1px solid var(--color-card-border)",
  whiteSpace: "nowrap" as const,
};

export const num = {
  textAlign: "right" as const,
  fontSize: 13,
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap" as const,
};

export const cell = { fontSize: 13, whiteSpace: "nowrap" as const };

export const totalRow = {
  "& td": {
    fontWeight: 700,
    borderTop: "2px solid var(--color-smoke)",
    borderBottom: "none",
    background: "var(--color-clear-dust)",
  },
};

export const tableBox = {
  border: "1px solid var(--color-card-border)",
  borderRadius: "var(--radius-card)",
  overflowX: "auto" as const,
  background: "var(--color-white)",
};

export const card = {
  border: "1px solid var(--color-card-border)",
  borderRadius: "var(--radius-card)",
  background: "var(--color-white)",
  p: 2.5,
};

/** One labelled figure in a summary card. */
export function Fact({
  label,
  children,
  tone,
}: {
  label: string;
  children: ReactNode;
  tone?: "warn" | "ok";
}) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: 11,
          letterSpacing: "0.6px",
          textTransform: "uppercase",
          color: "var(--color-graphite)",
          fontWeight: 600,
          mb: 0.75,
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          fontSize: 18,
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums",
          color:
            tone === "warn"
              ? "var(--color-error, #b3261e)"
              : tone === "ok"
                ? "var(--color-good-sage)"
                : "var(--color-rodeo-black)",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

/** A section heading with an optional note on the right. */
export function SectionHeading({ title, note }: { title: ReactNode; note?: ReactNode }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 1.5,
        flexWrap: "wrap",
        mb: 1.25,
      }}
    >
      <Typography component="h2" sx={{ fontSize: 16, fontWeight: 700 }}>
        {title}
      </Typography>
      {note && (
        <Typography sx={{ fontSize: 12, color: "var(--color-graphite)" }}>{note}</Typography>
      )}
    </Box>
  );
}

/** A back link at the top of a drill-down view. */
export function BackLink({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        appearance: "none",
        background: "none",
        border: "none",
        p: 0,
        minHeight: 36,
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: 13,
        fontWeight: 700,
        color: "var(--color-rodeo-black)",
      }}
    >
      ← {label}
    </Box>
  );
}
